const EMPTY_PHONE_TOKENS = new Set([
  "",
  "-",
  "--",
  "n/a",
  "na",
  "nil",
  "none",
  "not set",
  "null",
]);

const PHONE_PREFIX_PATTERN = /^(?:(?:m|mob|mobile|phone|ph|tel|telephone)\s*[:\-]\s*)+/i;

function normalizeWhitespace(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

function stripPhonePrefixes(value: string): string {
  let nextValue = value;
  let previousValue = "";
  while (nextValue !== previousValue) {
    previousValue = nextValue;
    nextValue = nextValue.replace(PHONE_PREFIX_PATTERN, "").trim();
  }
  return nextValue;
}

function sanitizePhoneText(value: unknown): string | null {
  const trimmed = stripPhonePrefixes(normalizeWhitespace(value));
  if (!trimmed) {
    return null;
  }

  const normalizedToken = trimmed.toLowerCase();
  if (EMPTY_PHONE_TOKENS.has(normalizedToken)) {
    return null;
  }

  return trimmed;
}

function coerceAuLocalDigits(digits: string): string {
  if (/^61[23784]\d{8}$/.test(digits)) {
    return `0${digits.slice(2)}`;
  }

  if (/^[23784]\d{8}$/.test(digits)) {
    return `0${digits}`;
  }

  return digits;
}

function formatRecognizedPhoneDigits(digits: string): string | null {
  if (/^04\d{8}$/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }

  if (/^0[2378]\d{8}$/.test(digits)) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }

  if (/^(?:1300|1800|0500)\d{6}$/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }

  if (/^13\d{4}$/.test(digits)) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
  }

  return null;
}

export function normalizePhoneDigits(value: unknown): string | null {
  const text = sanitizePhoneText(value);
  if (!text) {
    return null;
  }

  const digits = text.replace(/\D/g, "");
  if (!digits) {
    return null;
  }

  return coerceAuLocalDigits(digits);
}

export function normalizePhoneForStorage(value: unknown): string | null {
  const text = sanitizePhoneText(value);
  if (!text) {
    return null;
  }

  const digits = normalizePhoneDigits(text);
  if (!digits) {
    return text;
  }

  const formatted = formatRecognizedPhoneDigits(digits);
  if (formatted) {
    return formatted;
  }

  return /^[\d\s()+.-]+$/.test(text) ? digits : text;
}

export function formatPhoneForDisplay(value: unknown): string {
  return normalizePhoneForStorage(value) || "";
}
